import { isPlatformBrowser } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Inject,
  Input,
  OnDestroy,
  OnInit,
  Output,
  PLATFORM_ID, Signal
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';

import { Config, Facet, FeatureModulesService, Product, ScriptLoader } from '@spartacus/core';
import { CurrentProductService } from '@spartacus/storefront';
import { catchError, combineLatest, Observable, of, Subject, throwError } from 'rxjs';
import { delay, filter, switchMap, take, takeUntil } from 'rxjs/operators';
import { STORE_FINDER_FEATURE } from '@spartacus/storefinder/root';
import { TranslateService } from '@ngx-translate/core';
import { IStoreFilter, SdStoreLocatorComponent } from '@seb/web-design-system/locator';
import { IMapLegend } from '@seb/web-design-system/maps';
import { ICardStore, IOpeningHours, IPosition, IProductCard, ThemesEnum } from '@seb/web-design-system/core';
import { StoreFinderConfig } from '../../../config/configs/store-finder.config';
import {
  CustomPointOfService,
  RelayPoint,
  RepairPointOfService,
  RepairStoreFinderSearchPage
} from '../../../models';
import { ProductScopeEnum, StoreTypeEnum } from '../../../enums';
import { ProductMapper } from '../../../mappers';
import { BaseStoreService, HobStateService, RepairService, TrackingService } from '../../../services';
import { StoreFinderFacade } from '../../../facades';
import { computeDistanceKm, formatDistanceKm } from '../../../utils';
import { WeekdayOpeningDay } from '@spartacus/core/src/model/point-of-service.model';

@Component({
  selector: 'wc-store-locator',
  templateUrl: './store-locator.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    SdStoreLocatorComponent
  ]
})
export class StoreLocatorComponent implements OnInit, OnDestroy {
  titleKey: string;
  isLoaded = false;
  isPickup = false;
  relayPoints: RelayPoint[];

  mapLegend: IMapLegend[];
  stores: ICardStore[] = [];
  filteredStores: ICardStore[] = [];
  pointsOfService: RepairPointOfService[] = [];
  bounds: google.maps.LatLngBoundsLiteral;
  filters: IStoreFilter[];
  hasNoResults: boolean;

  product: IProductCard;
  selectedProduct: Product;

  searchCountryRestrictions: string | string[];

  centerOnCountry: { name: string; isocode: string };

  @Input()
  title: string;

  @Input()
  posTypes: string[];

  @Input()
  isRepair: boolean;

  @Input()
  isCheckout: boolean;

  @Input()
  isEmbedded: boolean;

  @Input()
  hideCardActions: boolean;

  @Input()
  posFeatures: string[];

  @Input()
  hideChooseButton = false;

  @Output()
  selectedRepairer = new EventEmitter<RepairPointOfService>();

  private readonly destroy$: Subject<void> = new Subject();

  readonly isHobSignal: Signal<boolean>;

  constructor(
    private config: Config,
    private scriptLoader: ScriptLoader,
    private storeFinderService: StoreFinderFacade,
    private currentProductService: CurrentProductService,
    private productMapper: ProductMapper,
    private readonly repairService: RepairService,
    private baseStoreService: BaseStoreService,
    private trackingService: TrackingService,
    private featureModulesService: FeatureModulesService,
    private translateService: TranslateService,
    private readonly hobStateService: HobStateService,
    private cdRef: ChangeDetectorRef,
    @Inject(PLATFORM_ID) private platformId: any
  ) {
    this.isHobSignal = toSignal(this.hobStateService.isHob$);
  }

  static relayPointToStoreCard(relayPoint: RelayPoint): ICardStore {
    return {
      name: relayPoint.name,
      address: relayPoint.address?.formattedAddress,
      position: { latitude: relayPoint.geoPoint?.latitude, longitude: relayPoint.geoPoint?.longitude },
      readonly: false
    };
  }

  private static storeToStoreCard(store: CustomPointOfService): ICardStore {
    return {
      id: store.name,
      formattedDistance: formatDistanceKm(store),
      name: store.displayName,
      type: StoreTypeEnum.STORE,
      address: store.address?.formattedAddress,
      telephone: store.address?.phone,
      url: store.url,
      ...(store.address?.email && { mail: store.address.email.includes(';') ?
        [...new Set((store.address.email).split(';').map((part) => part.trim()))] :
        new Array((store.address.email).trim())
      }),
      position: { latitude: store.geoPoint?.latitude, longitude: store.geoPoint?.longitude },
      readonly: false,
      openingHours: StoreLocatorComponent.mapOpeningHours(store.openingHours?.weekDayOpeningList)
    };
  }

  private static repairStoreToStoreCard(store: RepairPointOfService): ICardStore {
    return {
      id: store.name,
      name: store.displayName,
      type: StoreTypeEnum.STORE,
      address: store.address,
      telephone: store.telephone,
      ...(store?.email && { mail: store.email.includes(';') ?
        [...new Set((store.email).split(';').map((part) => part.trim()))] :
        new Array((store.email).trim())
      }),
      position: { latitude: store.latitude, longitude: store.longitude },
      formattedDistance: formatDistanceKm(store),
      readonly: false,
      openingHours: StoreLocatorComponent.mapOpeningHours(store.openingHours),
      services: store.storeFeatures.map((storeFeature) => {
        return {
          label: storeFeature
        };
      })
    };
  }

  private static toCoords(location: any): IPosition {
    return {
      latitude: location.location.lat,
      longitude: location.location.lng
    };
  }

  ngOnInit(): void {
    this.titleKey = this.isRepair ? 'storelocator.repairer.title' : 'storelocator.title';
    this.title = this.title ? this.title : this.translateService.instant(this.titleKey);
    this.filters = [];
    this.mapLegend = this.isRepair
      ? []
      : [
        {
          iconName: 'icon-pin-brand',
          description: `storelocator.legends.${StoreTypeEnum.STORE}`,
          isBrand: true,
          type: StoreTypeEnum.STORE
        }
      ];

    if (isPlatformBrowser(this.platformId)) {
      this.featureModulesService
        .resolveFeature(STORE_FINDER_FEATURE)
        .pipe(delay(500), takeUntil(this.destroy$))
        .subscribe(() => this.init());
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  onLocate(location: any): void {
    if (this.isRepair) {
      this.repairService
        .getRepairers(location.location.lat, location.location.lng, this.isCheckout, this.posFeatures, this.posTypes)
        .pipe(
          filter((state) => !!state),
          catchError((err) => {
            if (err.name === 'TimeoutError') {
              console.error('Timeout: the service took too long to respond.');
              this.hasNoResults = true;
              this.cdRef.markForCheck();
              return of(null);
            }

            console.error('Unknown eroor in getRepairers:', err);
            return throwError(() => err);
          }),
          takeUntil(this.destroy$)
        )
        .subscribe((storesPage: RepairStoreFinderSearchPage) => {
          this.pointsOfService = storesPage.stores;
          this.filters = this.facetToStoreLocatorFilter(storesPage.facets) || [];
          if (this.isCheckout) {
            this.filters = this.filters.filter((filter) => filter.state && filter.disabled);
          }
          this.pointsOfService = this.pointsOfService.map((pos) => ({
            ...pos,
            distanceKm: computeDistanceKm(
              { lat: location.location.lat, lng: location.location.lng },
              { lat: pos.latitude, lng: pos.longitude }
            )
          }));
          this.stores = this.pointsOfService?.map(StoreLocatorComponent.repairStoreToStoreCard) || [];
          this.hasNoResults = this.stores?.length === 0;
          this.applyFilters();
          if (
            (!!storesPage.boundNorthLatitude || !!storesPage.boundSouthLatitude) &&
            (!!storesPage.boundEastLongitude || !!storesPage.boundWestLongitude)
          ) {
            this.bounds = {
              north: storesPage.boundNorthLatitude,
              east: storesPage.boundEastLongitude,
              south: storesPage.boundSouthLatitude,
              west: storesPage.boundWestLongitude
            };
          }
          this.cdRef.markForCheck();
        });
    } else {
      const coords = StoreLocatorComponent.toCoords(location);
      this.storeFinderService.findStoresAction(
        '',
        {},
        coords,
        undefined,
        false,
        (this.config as StoreFinderConfig).googleMaps.radius
      );
    }
  }

  updateFilters(updatedFilters: IStoreFilter[]): void {
    this.filters = updatedFilters;
    this.applyFilters();
  }

  applyFilters(): void {
    let filteredPointsOfService: RepairPointOfService[] = this.pointsOfService;
    this.filters.forEach((eachFilter: IStoreFilter) => {
      if (eachFilter.state) {
        filteredPointsOfService = filteredPointsOfService.filter((filteredPointOfService: RepairPointOfService) => {
          return !!filteredPointOfService.storeFeatures?.find((singleEntry) => singleEntry === eachFilter.labelTitle);
        });
      }
    });
    this.filteredStores = filteredPointsOfService.map(StoreLocatorComponent.repairStoreToStoreCard) || [];
  }

  onProductClick(productCodeClicked: string): void {
    if (this.selectedProduct?.code === productCodeClicked) {
      const productEvent = this.trackingService.productToEventProduct(this.selectedProduct);
      this.trackingService.sendEvent('productClick', productEvent);
    }
  }

  selectRepairer(store: ICardStore): void {
    const repairer = this.pointsOfService.find((pointOfService) => pointOfService.name === store.id);
    if (repairer) {
      this.selectedRepairer.emit(repairer);
    }
    this.trackingService.sendEvent('reparatorChoice', {
      reparatorName: store.name
    });
  }

  private init(): void {
    if (typeof google === 'undefined' || typeof google.maps === 'undefined') {
      this.scriptLoader.embedScript({
        src: (this.config as StoreFinderConfig).googleMaps.apiUrl,
        params: { key: (this.config as StoreFinderConfig).googleMaps.apiKey, libraries: 'places,geometry' },
        callback: () => {
          this.isLoaded = true;
          this.cdRef.markForCheck();
        }
      });
    } else {
      this.isLoaded = true;
      this.cdRef.markForCheck();
    }

    this.currentProductService
      .getProduct(ProductScopeEnum.CARD)
      .pipe(
        switchMap((product) => {
          const productCard$: Observable<IProductCard> = product
            ? this.productMapper.productToProductCard(product)
            : of(null);
          return combineLatest([of(product), productCard$]);
        }),
        takeUntil(this.destroy$)
      )
      .subscribe(([product, productCard]) => {
        this.selectedProduct = product ? product : undefined;
        this.product = product ? productCard : undefined;
        this.cdRef.markForCheck();
      });

    const baseStore = this.baseStoreService.baseStore;
    const deliveryCountries = baseStore.deliveryCountries?.map((country) => country.isocode);
    this.searchCountryRestrictions = deliveryCountries?.length > 5 ? null : deliveryCountries;
    const countryInfo =
      this.searchCountryRestrictions?.indexOf(baseStore.market) !== -1
        ? baseStore.deliveryCountries?.find((country) => country.isocode === baseStore.market)
        : baseStore.deliveryCountries[0];
    this.centerOnCountry = { name: countryInfo?.name, isocode: countryInfo?.isocode };
    this.cdRef.markForCheck();

    this.storeFinderService
      .getFindStoresEntities()
      .pipe(
        take(1),
        filter((store) => !!store),
        takeUntil(this.destroy$)
      )
      .subscribe((storesPage: any) => {
        this.stores = storesPage.stores?.map(StoreLocatorComponent.storeToStoreCard) || [];
        if (
          (!!storesPage.boundNorthLatitude || !!storesPage.boundSouthLatitude) &&
          (!!storesPage.boundEastLongitude || !!storesPage.boundWestLongitude)
        ) {
          this.bounds = {
            north: storesPage.boundNorthLatitude,
            east: storesPage.boundEastLongitude,
            south: storesPage.boundSouthLatitude,
            west: storesPage.boundWestLongitude
          };
        }
        this.cdRef.markForCheck();
      });
  }

  private facetToStoreLocatorFilter(facets: Facet[]): IStoreFilter[] {
    if (!facets?.length) {
      return [];
    }
    return facets[0]?.values?.map((value) => {
      const names = value.name?.split('|');
      const labelTitle = names?.length > 0 ? names[0] : '';
      const nameField = names?.length > 2 ? names[2] : '';

      const isCertified = nameField === 'Centre_service_agree';
      const forcePrepaid = this.isCheckout && nameField === 'Reparation_forfaitaire_prepayee';

      return {
        labelTitle,
        nameField,
        type: facets[0]?.name,
        theme: isCertified ? ThemesEnum.default : ThemesEnum.dark,
        state: isCertified || forcePrepaid ? true : value.selected,
        disabled: forcePrepaid
      };
    });
  }

  onItenaryClick(link: string): void {
    this.trackingService.sendEvent('exitLink', {
      linkType: 'OthersLink',
      linkURL: link
    });
  }

  onLearnMoreClick(store: ICardStore): void {
    this.trackingService.sendEvent('reparatorPackageClick', {
      reparatorName: store.name
    });
  }

  private static mapOpeningHours(openingHours: WeekdayOpeningDay[]): IOpeningHours[] {
    let lastDuplicateDay;

    return openingHours?.reduce((acc, weekDayItem) => {
      const _weekDayItem = {
        closed: weekDayItem.closed,
        weekDay: weekDayItem?.weekDay,
        availability: [
          {
            openingTime: { formattedHour: weekDayItem.openingTime?.formattedHour },
            closingTime: { formattedHour: weekDayItem.closingTime?.formattedHour }
          }
        ]
      };
      const lastWeekDay = acc[acc.length - 1]?.weekDay;
      const currentWeekDay = weekDayItem?.weekDay;
      if (lastWeekDay === currentWeekDay || lastDuplicateDay === currentWeekDay) {
        if (currentWeekDay !== ' ') {
          lastDuplicateDay = currentWeekDay;
        }
        return [
          ...acc,
          {
            ..._weekDayItem,
            weekDay: ' '
          }
        ];
      }
      lastDuplicateDay = null;
      return [...acc, _weekDayItem];

    }, []);

  }
}
